import moment from 'moment'
import 'moment/locale/es'
import {cuentaMask, dineroDisabledMask} from '../Components/Items'
import reporteDeEstadoDePosicionFinancieraPdfV2 from './PdfV2.0/reporteDeEstadoDePosicionFinancieraPdfV2'
import {
    esModerno,
    traeSubtotales,
    agruparPorSubtipo,
    construirAnaliticas,
    filtrarSinSaldo,
    marcarJerarquia,
    sangriaDe,
} from './balanceGeneralAnaliticas'

moment.locale('es')

// Layout de las dos tablas de cuentas. El problema de que un renglon se partiera en
// dos lo resuelven los anchos 'auto'/'*' de abajo, no el apreton: aqui se le da AIRE
// (3.5pt arriba y abajo contra los 2 que trae pdfMake por omision) para que el balance
// no se lea amontonado.
const layoutCuentas = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 3.5,
    paddingBottom: () => 3.5,
}

// Cuenta e Importe se ajustan a su contenido ('auto') y la Descripcion se queda con lo
// que sobre ('*'). Con los porcentajes fijos de antes un codigo de nivel 5
// ("100-01-001-001-001") no cabia en la celda: ese codigo no tiene ningun punto de corte
// (UAX#14 no rompe antes de un guion entre digitos), asi que pdfMake lo partia POR
// CARACTER a mitad de grupo -> "100-01-001-0 / 01-001".
//
// Tiene que ser una FUNCION, no una constante compartida: pdfMake muta el array de
// widths (extendTableWidths lo convierte en objetos y buildColumnWidths les escribe
// _minWidth/_maxWidth/_calcWidth). Reusar el mismo array en dos tablas hace que la
// segunda pise las medidas de la primera y los anchos salgan del contenido equivocado.
const widthsCuentas = () => ['auto', '*', 'auto']

// El arbol se lee por tres senas a la vez: la SANGRIA de la descripcion (5pt por
// nivel), el PESO de la tipografia y el COLOR. Una cuenta es padre cuando la de abajo
// cuelga de ella, no por su profundidad: asi una cuenta de nivel 3 se ve como hoja
// cuando el filtro de nivel corta ahi, y como padre cuando sus hijos si salen.
const estiloCuenta = (fila) => {
    if (!fila.esPadre) return 'cuentaHoja'
    if (fila.profundidad === 1) return 'cuentaPadre1'
    if (fila.profundidad === 2) return 'cuentaPadre2'
    return 'cuentaPadre3'
}

// nivel1..nivel5 son String con default null en el modelo: hay que blindar el concat.
const codigoCuenta = (fila) =>
    cuentaMask(
        (fila.nivel1 || '') + (fila.nivel2 || '') + (fila.nivel3 || '') +
        (fila.nivel4 || '') + (fila.nivel5 || '')
    )

const filaCuenta = (fila) => {
    const style = estiloCuenta(fila)
    const sangria = sangriaDe(fila)
    return [
        {text: codigoCuenta(fila), style, alignment: 'left', noWrap: true},
        // La sangria va en la descripcion y no en el codigo: asi los codigos quedan
        // alineados y se pueden barrer con la vista, que es como se leen en cualquier
        // sistema contable.
        {text: fila.descripcion, style, alignment: 'left', margin: [sangria, 0, 0, 0]},
        {text: dineroDisabledMask(fila.saldo), style, alignment: 'right', noWrap: true},
    ]
}

const encabezadoColumnas = () => ([
    {text: 'Cuenta', style: 'tituloTabla'},
    {text: 'Descripción', style: 'tituloTabla'},
    {text: 'Importe', style: 'tituloTabla'},
])

const filaTituloSeccion = (texto) => ([
    {colSpan: 3, text: texto, style: 'tituloTabla1'},
    {},
    {},
])

const filaTituloGrupo = (texto) => ([
    {colSpan: 3, text: texto, style: 'tituloGrupo'},
    {},
    {},
])

const filaSubtotalGrupo = (etiqueta, monto) => ([
    {colSpan: 2, text: etiqueta, style: 'subtotalGrupo', alignment: 'left'},
    {},
    {text: dineroDisabledMask(monto), style: 'subtotalGrupo', alignment: 'right', noWrap: true},
])

const filaSumaSeccion = (etiqueta, monto) => ([
    {colSpan: 2, text: etiqueta, style: 'sumaSeccion', alignment: 'left'},
    {},
    {text: dineroDisabledMask(monto), style: 'sumaSeccion', alignment: 'right', noWrap: true},
])

/**
 * Cuerpo de una seccion (Activo o Pasivo). Con subtotales prendidos las cuentas se
 * reparten en grupos por subtipo SAT y cada grupo cierra con su total; el subtotal
 * impreso es el EXACTO que manda el backend, no la suma de los renglones visibles,
 * porque esos vienen podados por los filtros de saldo cero y de nivel.
 */
const cuerpoSeccion = (filas, seccion, conSubtotales, analiticas) => {
    if (!conSubtotales || !analiticas) {
        return filas.map(filaCuenta)
    }
    const grupos = agruparPorSubtipo(filas, seccion)
    // Sin grupos (pasa con el filtro "Nivel 1", donde todo es cuenta mayor) se cae al
    // render plano de siempre; el bloque de resumen del final igual sale.
    if (!grupos) return filas.map(filaCuenta)

    const cuerpo = []
    for (const grupo of grupos) {
        cuerpo.push(filaTituloGrupo(grupo.titulo))
        for (const fila of grupo.filas) cuerpo.push(filaCuenta(fila))
        cuerpo.push(filaSubtotalGrupo(grupo.etiquetaTotal, analiticas.subtotales[grupo.llaveTotal]))
    }
    return cuerpo
}

const reporteDeEstadoDePosicionFinancieraPdf = (values) => {
    const empresa = values.empresa
    const tipoPdf = values.tipoPdfBalance

    // Plantilla elegida en el menu del boton PDF. La guarda del logo copia la de
    // polizasTrazabilidadPdf: PDFHeaderV2 referencia image:"logo" siempre y un base64
    // vacio truena pdfMake, mientras que el encabezado clasico si sabe omitirlo.
    if (esModerno(tipoPdf) && empresa?.logo?.base64Completa) {
        return reporteDeEstadoDePosicionFinancieraPdfV2(values)
    }

    const analiticas = construirAnaliticas(values)
    const conSubtotales = traeSubtotales(tipoPdf) && !!analiticas
    const tieneLogo = !!empresa?.logo?.base64Completa

    let filtrados = []

    if(values.filtrarProyecto === 1){
        let textoFiltrado = ''
        values.proyectos.map((valueProyectos) => {
            textoFiltrado += `${valueProyectos.nombre}\n`
            return null
        })
        filtrados.push([
            {text: 'Proyectos:', style: 'subheader'},

            {text: textoFiltrado, style: 'textoChico'}
          ])
    }

    if(values.filtrarProyecto === 2){
        filtrados.push([
            {text: 'Unidad de Negocio:', style: 'subheader'},

            {text: `${cuentaMask(
                values.unidadDeNegocio.nivel1 + values.unidadDeNegocio.nivel2 + values.unidadDeNegocio.nivel3 + values.unidadDeNegocio.nivel4 +values.unidadDeNegocio.nivel5
              ) +
                " - " +
                values.unidadDeNegocio.nombre}`, style: 'textoChico'}
          ])
    }

    if(filtrados.length === 0){
        filtrados.push([
            {text: 'No tiene filtrados adicionales', style: 'subheader'},

            {text: '', style: 'textoChico'}
      ] )
    }

    let totalActivo = 0
    let totalPasivo = 0
    let totalCapital = 0
    values.arrayActivo.forEach(valueActivo => {
        if(valueActivo.nivel === 1){
            totalActivo += valueActivo.saldo
        }
    })
    values.arrayPasivo.forEach(valuePasivo => {
        if(valuePasivo.nivel === 1){
            totalPasivo += valuePasivo.saldo
        }
    })
    values.arrayCapital.forEach(valueCapital => {
        if(valueCapital.nivel === 1){
            totalCapital += valueCapital.saldo
        }
    })

    // Los totales se calculan sobre el arreglo COMPLETO, antes de quitar las cuentas sin
    // saldo, para que esconder un renglon nunca mueva una suma.
    const filasActivo = marcarJerarquia(filtrarSinSaldo(values.arrayActivo))
    const filasPasivo = marcarJerarquia(filtrarSinSaldo(values.arrayPasivo))
    const filasCapital = marcarJerarquia(filtrarSinSaldo(values.arrayCapital))

    const activoTable = cuerpoSeccion(filasActivo, 'activo', conSubtotales, analiticas)
    const pasivoTable = cuerpoSeccion(filasPasivo, 'pasivo', conSubtotales, analiticas)
    // Capital tiene un solo subtipo ("Capital contable"), asi que no se agrupa: se pinta
    // plano como siempre, con su "Utilidad del ejercicio" sintetica al final.
    const capitalTable = filasCapital.map(filaCuenta)

    // El analisis (totales, % de integracion y razones) ya no vive aqui: se descarga
    // como un documento aparte, `anexoAnalisisBalancePdf`. Esta hoja se queda siendo
    // el balance y nada mas.

    var dd = {
        info: {
            title: 'Estado de Posición Financiera',
            author: 'BIWO ERP',
            subject: 'Estado de Posición Financiera',
            creator: 'pdfmake',
        },
        content: [
            {
                style: 'tableExample',
                table: {
                    widths: [ '18%', '52%', '30%'],
                    layout: {
                        defaultBorder: false,
                    },
                    body: [
                        [
                            // Sin logo NO se puede emitir el nodo `image`: pdfMake trata la
                            // cadena vacia como una ruta de archivo y truena el PDF entero
                            // con "Invalid image", sin mensaje para el usuario.
                            tieneLogo
                                ? {border: [false, false, false, false], image: 'logo', fit: [80, 80]}
                                : {border: [false, false, false, false], text: ''},
                            {
                            //	rowSpan: 2,
                                border: [false, false, false, false],
                                table: {
                                    headerRows: 1,
                                    widths: ['100%'],

                                    body: [
                                      [
                                          {
                                          text: empresa.nombreComercial ? empresa.nombreComercial : (empresa.persona ? empresa.nombre : empresa.nombre+' '+empresa.apellidoPaterno+' '+empresa.apellidoMaterno),
                                          style: "tableHeader"
                                          }
                                      ],
                                      [
                                        {
                                          text: "R.F.C: "+empresa.rfc,
                                          style: "textoChico",
                                          alignment: "center",
                                        },
                                      ],
                                      [
                                          {
                                            text: empresa.calle+' '+
                                                  empresa.numeroCalle+' '+
                                                  (empresa.numeroInterior ? empresa.numeroInterior : '')
                                            ,
                                            style: "textoChico",
                                            alignment: "center",
                                          },
                                        ],
                                      [
                                          {
                                            text: empresa.colonia.tipoAsentamiento+' '+
                                            empresa.colonia.nombre+' C.P. '+
                                            empresa.colonia.cp+' '+
                                            empresa.municipio.nombre+' '+
                                            empresa.estado.abreviatura+' '+
                                            empresa.pais.abreviatura+' '
                                            ,
                                            style: "textoChico",
                                            alignment: "center",
                                          },
                                        ],
                                        // El titulo del reporte ya va en su propia banda mas abajo;
                                        // repetirlo aqui solo gastaba un renglon del encabezado.
                                    ]
                                },
                                layout: 'noBorders'
                            },
                            values.ocultarDatosFiltrado
                                ? {text: '', border: [false, false, false, false]}
                                : {
                                border: [false, false, false, false],
                                    table: {
                                    headerRows: 1,
                                    widths: ['100%'],

                                    body: [
                                        [{text: 'Fecha Final:', style: 'subheader', alignment: 'center'}],
                                        [{text: moment(values.fechaFinal).locale('es').format('LL'), style: 'textoChico', alignment: 'center'}],
                                        [{text: 'Fecha de Impresión:', style: 'subheader', alignment: 'center'}],
                                        [{text: moment().locale('es').format('LL'), style: 'textoChico', alignment: 'center'}],
                                    ]
                                },
                                layout: 'noBorders'
                            },
                        ]

                    ]
                },
            },
            ...(values.ocultarDatosFiltrado ? [] : [
            {text: 'Datos de filtrado:'},
            {
                style: 'tableExample',
                table: {
                    widths: ['20%', '80%'],
                    layout: {
                                        defaultBorder: false,
                                    },

                    body: filtrados,
                },
                            layout: {
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.body.length) ? 1 : 1;
                    },
                    vLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.widths.length) ? 1 : 1;
                    },
                    hLineColor: function (i, node) {
                        return '#adaaaa';
                    },
                    vLineColor: function (i, node) {
                        return '#adaaaa';
                    },
                    hLineStyle: function (i, node) {
                        if (i === 0 || i === node.table.body.length) {
                            return null;
                        }
                        return {dash: {length: 2, space: 1}};
                    },
                    vLineStyle: function (i, node) {
                        if (i === 0 || i === node.table.widths.length) {
                            return null;
                        }
                        return {dash: {length: 2}};
                    },
                    // paddingLeft: function(i, node) { return 4; },
                    // paddingRight: function(i, node) { return 4; },
                    // paddingTop: function(i, node) { return 2; },
                    // paddingBottom: function(i, node) { return 2; },
                    // fillColor: function (i, node) { return null; }
                }

            },
            ]),
            {
                style: 'tableExample',
                table: {
                    widths: ['100%'],
                    layout: {
                        defaultBorder: false,
                     },
                    body: [
                            [
                              {text: 'ESTADO DE POSICION FINANCIERA', alignment: 'center', fontSize: 16, border: [false, false, false, false]}
                            ]
                       ]
                },
                            layout: {
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.body.length) ? 1 : 1;
                    },
                    vLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.widths.length) ? 1 : 1;
                    },
                    hLineColor: function (i, node) {
                        return '#adaaaa';
                    },
                    vLineColor: function (i, node) {
                        return '#adaaaa';
                    },
                    hLineStyle: function (i, node) {
                        if (i === 0 || i === node.table.body.length) {
                            return null;
                        }
                        return {dash: {length: 2, space: 1}};
                    },
                    vLineStyle: function (i, node) {
                        if (i === 0 || i === node.table.widths.length) {
                            return null;
                        }
                        return {dash: {length: 2}};
                    },
                }

            },

            {
                columns: [
                    {
                        width: '49.5%',
                        table: {
                            // Titulo de la seccion + encabezado de columnas: se repiten al
                            // pasar de pagina, que antes no pasaba (no habia headerRows).
                            headerRows: 2,
                            widths: widthsCuentas(),
                            body: [
                                filaTituloSeccion('ACTIVO'),
                                encabezadoColumnas(),
                                ...activoTable,
                                // La columna del Pasivo siempre cerro con sus dos sumas y
                                // la del Activo no cerraba con ninguna: el unico total del
                                // Activo vivia en el pie, que cuando el reporte pasa de
                                // hoja queda lejisimos de su columna.
                                filaSumaSeccion('Suma del Activo', totalActivo),
                            ]
                        },
                        layout: layoutCuentas
                    },
                    {
                        width: '1%',
                        text: ''
                    },
                        {
                        width: '49.5%',
                        table: {
                            headerRows: 2,
                            widths: widthsCuentas(),
                            body: [
                                filaTituloSeccion('PASIVO'),
                                encabezadoColumnas(),
                                ...pasivoTable,
                                filaSumaSeccion('Suma del Pasivo', totalPasivo),
                                filaTituloSeccion('CAPITAL'),
                                encabezadoColumnas(),
                                ...capitalTable,
                                filaSumaSeccion('Suma del Capital', totalCapital),
                            ]
                        },
                        layout: layoutCuentas
                    }
                ],
                style: 'tableExample',
            },
            {
                alignment: 'right',
              table: {
                  widths: ['10%', '19.5%', '20%', '1%', '10%', '19.5%', '20%'],
                  layout: {
                    defaultBorder: false,
                  },
                  body: [
                      [
                        {border: [false,false, false,false], text: 'Suma del Activo:', style: 'subheader', colSpan: 2},
                        {border: [false,false, false,false], text: '', style: 'subheader',},
                        {border: [false,true, false,false], text: dineroDisabledMask(totalActivo), style: 'subheader', noWrap: true},
                        {border: [false,false, false,false], text: '', style: 'subheader',},
                        {border: [false,false, false,false], text: 'Suma del Pasivo y Capital:', style: 'subheader', colSpan: 2},
                        {border: [false,false, false,false], text: '', style: 'textoChico',},
                        {border: [false,true, false,false], text: dineroDisabledMask(totalPasivo+totalCapital), style: 'textoChico', alignment: 'right', noWrap: true}
                      ],
                  ]
              },
            },
        ],
        ...(tieneLogo ? {images: {logo: empresa.logo.base64Completa}} : {}),
        styles: {
            header: {
                fontSize: 9,
                bold: true,
            },
            subheader: {
                fontSize: 8,
                bold: true,
            },
            tableExample: {
                margin: [0, 5, 0, 5]
            },
            tablaSinMargen: {
                margin: [0, 0, 0, 0]
            },
            tableHeader: {
                bold: true,
                fontSize: 12,
                color: 'black',
                alignment: 'center'
            },
            textoChico:{
                fontSize: 8
            },
            tituloTabla:{
                fontSize: 8,
                bold: true,
                fillColor: '#cccccc',
                alignment: 'center',
            },
            // ── Escala del arbol de cuentas ──────────────────────────────────
            // Tres senas que se refuerzan: tinte de fondo en los dos primeros
            // niveles, negritas en todos los padres, y un azul contable en los
            // padres profundos, que ya no llevan fondo para no ensuciar la hoja.
            // Las hojas van en tinta normal: son el dato, no la estructura.
            // Los grises van escalonados de claro a oscuro para que no compitan con
            // los de las filas estructurales: cuenta mayor #E8E8E8 < encabezado de
            // grupo #D9D9D9 < suma de seccion #CCCCCC.
            cuentaPadre1: {
                fontSize: 8.5,
                bold: true,
                fillColor: '#E8E8E8',
                color: '#1F2937',
            },
            cuentaPadre2: {
                fontSize: 8,
                bold: true,
                fillColor: '#F4F4F4',
                color: '#1F2937',
            },
            cuentaPadre3: {
                fontSize: 8,
                bold: true,
                color: '#1F6F8B',
            },
            cuentaHoja: {
                fontSize: 8,
                color: '#333333',
            },
            tituloGrupo: {
                fontSize: 8.5,
                bold: true,
                fillColor: '#D9D9D9',
                alignment: 'center',
                // Mas aire arriba que abajo: separa el grupo del subtotal anterior sin
                // despegarlo de las cuentas que encabeza.
                margin: [0, 5, 0, 2],
            },
            subtotalGrupo: {
                fontSize: 8,
                bold: true,
                fillColor: '#F0F0F0',
            },
            sumaSeccion: {
                fontSize: 9,
                bold: true,
                fillColor: '#CCCCCC',
            },
            tituloTabla1:{
                fontSize: 12,
                bold: true,
                alignment: 'center',
            },
            textoTabla: {
                fontSize: 8,
                italics: false
            }
        },
        pageSize: 'LETTER',
        // Sin pageMargins pdfMake usa 40 por lado y deja 532pt utiles, que repartidos en
        // dos columnas no alcanzan para un codigo de cuenta de nivel 5. Con 28 quedan 556.
        pageMargins: [28, 32, 28, 32],
    }
  return dd;
};


export default reporteDeEstadoDePosicionFinancieraPdf;
